import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
    Card,
    Form,
    Input,
    InputNumber,
    Select,
    DatePicker,
    Button,
    Row,
    Col,
    Typography,
    Table,
    Tag,
    message,
    Space,
    Divider,
    Alert,
    Spin,
    Popconfirm,
    Modal,
} from 'antd';
import { SaveOutlined, FileAddOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTheme } from '../../context/ThemeContext';
import { useModuleApi } from '../ortak/moduleApi';
import { formatMoney, inputNumberMoneyProps } from '../../utils/currency';
import {
    HesapAciklamasi, UyariListesi, useCariSecenekleri, cariKartiAc, cakismaOnayliGonder, hataMesaji, AY_ADLARI,
    benzerCariler, cariAcBaslik, tutarKontrolNedeni, referansDagit, TutarKontrolKutusu,
} from '../sozlesme/OdemeTalebiModal';

// 26.10 düzeltmesi (F2/F3/F6): /faturalar YS 'Görüntüle' sonrası açılan YS kira ödeme ekranı.
// Önceden generic /form/submit-form ile hissedarsız, mükerrer kontrolsüz gider_fatura_talebi Insert ediyor,
// tutarı boş yıl kolonundan (0 TL) ve dönemi 2022'de duran defterden öneriyordu. Artık:
// * tutar / dönem / artış açıklaması: GET /contracts/ys-invoice/calc (sözleşme kuralı, canlı ödemelerle doğrulandı),
// * banka girdikçede ysk seçimi backend'de deterministik (id = bsk.id),
// * kayıt: POST /contracts/ys-invoice/request-payment — hissedar başına talep + defter + Finans ödeme talebi,
//   yalnız Ödeme Vadesi istenir (fatura no / fatura tarihi yok); mükerrer ve eski Fedora çift ödeme onayı.
// 27.10 (inceleme): baz güvenilir değilse (ör. girdikçede sözleşme toplamı) tutar önerilmez; referans
//   gösterilir, tutar elle girilir ve 'tutarı kontrol ettim' onayı istenir (sunucu da 409 'tutar_kontrol').
// 27.10 düzeltmesi: mevcut talepler calc'ın mukerrer_uyarilari'ndan, kayıt uçlarıyla AYNI sınıflandırmayla
//   gösterilir — aynı cari / hissedar olmayan adla ödenmiş-açık talep / sözleşme toplamının diğer lokasyonları
//   'engel' (kırmızı; kayıtta 409 + açık onay), diğer hissedarlar 'bilgi'. Eski calc yanıtında eski görünüm.

const { Title, Text } = Typography;

const paymentTypes = {
    bos: 'Boş Kabin Ödemesi',
    banka: 'Banka Girdikçe Kira Ödemesi',
    sabit: 'Sabit Kira Ödemesi',
};

const invoiceTypeOptions = [
    { value: 1, label: 'Sabit Kira' },
    { value: 2, label: 'Banka Başı Kira Gideri (girdikçe)' },
    { value: 3, label: 'Boş Kabin Kirası' },
];

const locationStatusOptions = {
    0: 'Pasif / Silinmiş',
    1: 'Pasif / Aday',
    2: 'Kurulum Bekliyor',
    3: 'Aktif',
    4: 'Pasif / Kaldırılmış',
    5: 'Pasif / Deviredilmiş',
    6: 'Pasif / Keşif',
    99: 'Offsite',
};

const contractSignatureStatusOptions = {
    1: 'İmzalandı',
    2: 'İmzalandı (Rotasyon)',
    6: 'Geçici Onay',
};

// Mongo form 226 (sozlesme-fatura.md §5.1) — önceki sürüm bu kodları farklı etiketliyordu
const ARTIS_SEKLI = { 0: 'Yok', 1: 'Yıllık Ortalama', 2: 'Önceki Yıl Aynı Ay', 3: '12 Aylık Ortalama' };
const ARTIS_ORANI = {
    0: 'Artış Yok', 1: 'ÜFE', 2: 'TÜFE', 3: '(ÜFE+TÜFE)/2', 4: 'İTO/TEFE', 5: 'Sabit Oran',
    6: '(ÜFE+TÜFE)/2 + Sabit', 7: 'Encümen', 8: 'Özel', 9: 'EuroZone', 10: 'Yeniden Değerleme',
};
const ODEME_SEKLI = { 0: 'Yıllık Peşin', 1: 'Aylık Peşin', 2: 'Özel' };

const formatDate = (d) => (d ? dayjs(d).format('DD.MM.YYYY') : '-');

const BankaFaturaIslem = () => {
    const { id } = useParams();
    const location = useLocation();
    const { darkMode } = useTheme();
    const api = useModuleApi('/contracts');
    const [form] = Form.useForm();
    const [modal, contextHolder] = Modal.useModal();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [hissedarlar, setHissedarlar] = useState([]);
    const [tutarOnay, setTutarOnay] = useState(false);

    const urlParams = new URLSearchParams(location.search);
    const tip = ['sabit', 'bos', 'banka'].includes(urlParams.get('tip')) ? urlParams.get('tip') : 'banka';
    const month = urlParams.get('m') ? parseInt(urlParams.get('m'), 10) : new Date().getMonth() + 1;
    const year = urlParams.get('y') ? parseInt(urlParams.get('y'), 10) : new Date().getFullYear();

    const { options: cariOpts, reload: cariReload, finApi } = useCariSecenekleri(true);

    const loadData = useCallback(async () => {
        if (!id) {
            setError('ID parametresi bulunamadı. Lütfen geçerli bir kayıt seçin.');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            // banka girdikçede id = bsk.id; ysk backend'de lokasyon başına deterministik seçilir
            const d = await api.get(`/ys-invoice/calc/${tip === 'banka' ? 0 : id}`, {
                tip, yil: year, ay: month, ...(tip === 'banka' ? { bsk_id: id } : {}),
            });
            setData(d);
            setTutarOnay(false);
            setHissedarlar((d.hissedarlar || []).map((h, i) => ({ ...h, _k: i })));
            const bas = d.donem_baslangic ? dayjs(d.donem_baslangic) : null;
            form.setFieldsValue({
                donemBaslangic: bas,
                donemBitis: d.donem_bitis ? dayjs(d.donem_bitis) : null,
                faturaTuru: d.fatura_turu,
                vade: bas && bas.isAfter(dayjs()) ? bas : dayjs(),
            });
        } catch (err) {
            setError('Veri yüklenirken hata oluştu: ' + hataMesaji(err, err.message));
        } finally {
            setLoading(false);
        }
    }, [api, id, tip, year, month, form]);

    useEffect(() => { loadData(); }, [loadData]);

    const setHisse = (k, alan, val) => setHissedarlar((arr) => arr.map((h) => (h._k === k ? { ...h, [alan]: val } : h)));
    const hisseToplam = hissedarlar.reduce((s, h) => s + (Number(h.tutar) || 0), 0);
    const tutarNedeni = tutarKontrolNedeni(data, hisseToplam);

    const hisseCariAc = async (h) => {
        try {
            const yeniId = await cariKartiAc(finApi, h.cari_adi, h.iban);
            await cariReload();
            if (yeniId) setHisse(h._k, 'cari_id', yeniId);
            message.success('Cari kartı açıldı');
        } catch (e) {
            message.error(e?.response?.status === 403
                ? 'Cari kartı açma yetkiniz yok — Finans → Cari ekranından açılmalı' : hataMesaji(e, 'Cari kartı açılamadı'));
        }
    };

    const handleSubmit = async (odemeTalebi) => {
        let values;
        try { values = await form.validateFields(); } catch { return; }
        if (odemeTalebi && hissedarlar.some((h) => !h.cari_id)) {
            message.warning('Her hissedar için Finans cari kartı seçin (yoksa "Kart aç")');
            return;
        }
        if (hissedarlar.some((h) => !(Number(h.tutar) > 0))) { message.warning('Her hissedarın tutarını girin (0\'dan büyük)'); return; }
        if (tutarNedeni && !tutarOnay) { message.warning('Tutarı kontrol edip "Tutarı kontrol ettim" kutusunu işaretleyin'); return; }
        const govde = {
            ysk_id: data.ysk_id,
            bsk_id: data.bsk_id || undefined,
            lokasyon: data.lokasyon,
            kabin: data.kabin,
            firma: data.firma,
            banka: data.banka,
            donem_baslangic: values.donemBaslangic.format('YYYY-MM-DD'),
            donem_bitis: values.donemBitis.format('YYYY-MM-DD'),
            fatura_turu: values.faturaTuru,
            fatura_tutar: Math.round(hisseToplam * 100) / 100,
            lines: hissedarlar.map((h) => ({ cari_adi: h.cari_adi, tutar: h.tutar, cari_id: h.cari_id, iban: h.iban || null })),
            ...(odemeTalebi ? { vade: values.vade.format('YYYY-MM-DD') } : {}),
            tutar_kontrol_onay: !!(tutarNedeni && tutarOnay),
        };
        setSaving(true);
        try {
            const res = await cakismaOnayliGonder(modal, (force) => api.post(
                odemeTalebi ? '/ys-invoice/request-payment' : '/ys-invoice/request', { ...govde, force }));
            if (!res) return;
            message.success(res.message || 'Kaydedildi');
            loadData();
        } catch (err) {
            message.error('Kaydetme sırasında hata oluştu: ' + hataMesaji(err, err.message));
        } finally {
            setSaving(false);
        }
    };

    if (loading && !data) {
        return (
            <div style={{
                display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh',
                background: darkMode ? 'var(--dark-bg)' : 'var(--color-bg)',
            }}>
                <Spin size="large" />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '20px' }}>
                <Alert message="Hata" description={error} type="error" showIcon />
            </div>
        );
    }

    if (!data) {
        return (
            <div style={{ padding: '20px' }}>
                <Alert message="Veri Bulunamadı" description="YS kira ödeme işlemi için gerekli veriler yüklenemedi." type="warning" showIcon />
            </div>
        );
    }

    const b = data.bilgi || {};
    const cardStyle = {
        backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined,
        borderColor: darkMode ? 'var(--dark-border)' : undefined,
    };
    const ayAdi = AY_ADLARI[(month || 1) - 1];

    return (
        <div className={`banka-fatura-page ${darkMode ? 'dark-theme' : ''}`}
            style={{ padding: '20px', background: darkMode ? 'var(--dark-bg)' : 'var(--color-bg)', minHeight: '100vh' }}>
            {contextHolder}
            <Card className={darkMode ? 'dark-card' : ''} style={cardStyle}>
                <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
                    <Title level={3} style={{ color: darkMode ? 'var(--dark-text)' : undefined, margin: 0 }}>
                        {tip === 'banka'
                            ? `${data.lokasyon_ad || data.lokasyon} | ${data.kabin}. Kabin ${data.banka_ad || 'Banka ?'} | ${paymentTypes[tip]} | ${ayAdi} ${year}`
                            : `${data.lokasyon_ad || data.lokasyon} | ${paymentTypes[tip]} | ${ayAdi} ${year}`}
                    </Title>
                    <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>Yenile</Button>
                </Space>

                <Divider />

                <Row gutter={[24, 24]}>
                    <Col xs={24} lg={10}>
                        <Card title="Lokasyon / Sözleşme Bilgileri" size="small" className={darkMode ? 'dark-card' : ''} style={cardStyle}>
                            <Table
                                rowKey="key"
                                columns={[
                                    { title: '', dataIndex: 'key', key: 'k', width: 190 },
                                    { title: '', dataIndex: 'value', key: 'v' },
                                ]}
                                dataSource={[
                                    { key: 'İl / İlçe', value: `${b.il || 'Bilinmiyor'} / ${b.ilce || 'Bilinmiyor'}` },
                                    { key: 'Lokasyon No', value: data.lokasyon },
                                    { key: 'Lokasyon Durumu', value: locationStatusOptions[b.lokasyon_durum] || 'Bilinmiyor' },
                                    { key: 'Yer Sahibi', value: b.mUnvan || '-' },
                                    { key: 'YS Sözleşme No', value: b.sozlesmeNo || '-' },
                                    {
                                        key: 'YS Sözleşme Baş. / Bit. | İmza',
                                        value: `${formatDate(b.ys_baslangic)} / ${formatDate(b.ys_bitis)} | ${contractSignatureStatusOptions[b.ys_imza] || b.ys_imza || '-'}`,
                                    },
                                    { key: 'Kira Başlangıç', value: formatDate(b.kira_baslangic) },
                                    ...(tip === 'banka' ? [{
                                        key: 'Banka Sözleşme Baş. / Bit. | İmza',
                                        value: `${formatDate(b.banka_baslangic)} / ${formatDate(b.banka_bitis)} | ${contractSignatureStatusOptions[b.banka_imza] || b.banka_imza || '-'}`,
                                    }, { key: 'Temlik', value: b.temlik ? 'Var' : 'Yok' }] : []),
                                    {
                                        key: 'Artış Şekli / Oranı',
                                        value: `${ARTIS_SEKLI[b.artisSekli] || b.artisSekli || '-'} / ${ARTIS_ORANI[b.artisOrani] || b.artisOrani || '-'}`
                                            + (Number(b.artisOrani) === 5 || Number(b.artisOrani) === 6 ? ` (sabit %${b.sabitOran || 0})` : '')
                                            + (b.min_artis || b.max_artis ? ` [min %${b.min_artis || 0} / max %${b.max_artis || 0}]` : ''),
                                    },
                                    { key: 'Ödeme Şekli', value: ODEME_SEKLI[b.odemeSekli] || '-' },
                                    { key: 'KDV / Stopaj', value: `${data.kdv ? 'KDV' : '-'} / ${data.stopaj ? 'Stopaj' : '-'}` },
                                    { key: 'Son Talep Dönem Bitişi', value: formatDate(data.son_fatura_tarihi) },
                                ]}
                                pagination={false}
                                showHeader={false}
                                size="small"
                                className={darkMode ? 'dark-table' : ''}
                            />
                        </Card>
                    </Col>

                    <Col xs={24} lg={14}>
                        <Card title="Kira Hesabı (Artış ne zaman?)" size="small" className={darkMode ? 'dark-card' : ''} style={cardStyle}>
                            <HesapAciklamasi data={data} />
                            {Array.isArray(data.mukerrer_uyarilari) ? (
                                <UyariListesi uyarilar={data.mukerrer_uyarilari} />
                            ) : (data.mevcut_talepler || []).length > 0 && (
                                <UyariListesi uyarilar={[{ kod: 'mevcut', seviye: 'uyari', mesaj: 'Bu dönem için mevcut talepler', satirlar: data.mevcut_talepler }]} />
                            )}
                            {(data.eski_fedora_odemeler || []).length > 0 && (
                                <UyariListesi uyarilar={[{ kod: 'eski_fedora', seviye: 'engel', mesaj: 'Eski Fedora: bu dönem için kira ödemesi var — ÇİFT ÖDEME riski', satirlar: data.eski_fedora_odemeler }]} />
                            )}
                        </Card>
                    </Col>

                    <Col xs={24}>
                        <Card title="Ödeme Talebi" size="small" className={darkMode ? 'dark-card' : ''} style={cardStyle}>
                            <Form form={form} layout="vertical" className={darkMode ? 'dark-form' : ''}>
                                <Row gutter={[16, 0]}>
                                    <Col xs={24} sm={6}>
                                        <Form.Item label="Dönem Başlangıç" name="donemBaslangic" rules={[{ required: true, message: 'Başlangıç tarihi gerekli' }]}>
                                            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={6}>
                                        <Form.Item label="Dönem Bitiş" name="donemBitis" rules={[{ required: true, message: 'Bitiş tarihi gerekli' }]}>
                                            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={6}>
                                        <Form.Item label="Ödeme Vadesi" name="vade" rules={[{ required: true, message: 'Ödeme vadesi zorunlu' }]}>
                                            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={6}>
                                        <Form.Item label="Tür" name="faturaTuru" rules={[{ required: true }]}>
                                            <Select disabled options={invoiceTypeOptions} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Form>
                            <TutarKontrolKutusu neden={tutarNedeni} data={data} onay={tutarOnay} setOnay={setTutarOnay}
                                onDagit={() => setHissedarlar((arr) => referansDagit(arr))} />
                            <Table rowKey="_k" size="small" pagination={false} dataSource={hissedarlar}
                                title={() => (
                                    <span>
                                        Hissedarlar (her biri ayrı talep + Finans ödeme talebi) — toplam <b>{formatMoney(hisseToplam)}</b>
                                        {Math.abs(hisseToplam - (data.fatura_tutar || 0)) > 0.05 && <Tag color="orange" style={{ marginLeft: 6 }}>hesaplanandan farklı</Tag>}
                                    </span>
                                )}
                                columns={[
                                    { title: 'Cari Adı', dataIndex: 'cari_adi', render: (v) => v || <Tag color="red">belirtilmemiş — talep oluşturulamaz</Tag> },
                                    {
                                        title: 'Finans Cari Kartı', dataIndex: 'cari_id', width: 330, render: (v, r) => (
                                            <Space size={4}>
                                                <Select size="small" showSearch allowClear optionFilterProp="label" options={cariOpts}
                                                    value={v || undefined} placeholder="Cari ara..." style={{ width: 240 }}
                                                    status={v ? undefined : 'warning'} onChange={(cid) => setHisse(r._k, 'cari_id', cid)} />
                                                {!v && r.cari_adi && (
                                                    <Popconfirm title={cariAcBaslik(r.cari_adi, benzerCariler(cariOpts, r.cari_adi))} okText="Aç" cancelText="Vazgeç" onConfirm={() => hisseCariAc(r)}>
                                                        <Button size="small" type="dashed" icon={<PlusOutlined />}>Kart aç</Button>
                                                    </Popconfirm>
                                                )}
                                            </Space>
                                        ),
                                    },
                                    { title: 'IBAN', dataIndex: 'iban', width: 230, render: (v, r) => <Input size="small" value={v || ''} placeholder="TR.." onChange={(e) => setHisse(r._k, 'iban', e.target.value)} /> },
                                    { title: 'Oran', dataIndex: 'oran', width: 80, render: (v) => `%${((v || 0) * 100).toFixed(1)}` },
                                    {
                                        title: 'Tutar', dataIndex: 'tutar', width: 170, render: (v, r) => (
                                            <InputNumber {...inputNumberMoneyProps} size="small" value={v} step={0.01} min={0}
                                                status={Number(v) > 0 ? undefined : 'error'} placeholder={data.tutar_onerildi === false ? 'elle girin' : undefined}
                                                onChange={(val) => setHisse(r._k, 'tutar', val)} />
                                        ),
                                    },
                                ]} />
                            <Space style={{ marginTop: 12 }}>
                                <Button type="primary" icon={<FileAddOutlined />} loading={saving} onClick={() => handleSubmit(true)}>
                                    Kaydet ve Ödeme Talebi Oluştur
                                </Button>
                                <Button icon={<SaveOutlined />} loading={saving} onClick={() => handleSubmit(false)}>
                                    Yalnız Talep Kaydet
                                </Button>
                                <Text type="secondary">Fatura no / fatura tarihi istenmez; ödeme talebi Finans → Ödeme Talebi ekranına düşer.</Text>
                            </Space>
                        </Card>
                    </Col>
                </Row>
            </Card>
        </div>
    );
};

export default BankaFaturaIslem;
